import { useState, useEffect, useCallback } from 'react';
import {
  BarChart3,
  TrendingUp,
  Car,
  DollarSign,
  Users,
  Clock,
  Loader2,
  Download,
  Calendar,
} from 'lucide-react';
import { api } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { PageHeader } from '../../components/ui';
import { StatCard, Panel, EmptyState, Badge, buttonClass } from '../../design-system';
import { formatCurrency, formatNumber } from '../../utils/format';

interface VendasPeriodo {
  dataInicio: string;
  dataFim: string;
  agrupamento: string;
  vendasPorPeriodo: { periodo: string; quantidade: number; valorTotal: number; ticketMedio: number }[];
  totais: { totalVendas: number; valorTotal: number; ticketMedio: number; totalComissoes: number };
}

interface VendaPorVendedor {
  vendedorId: number;
  vendedorNome: string;
  quantidadeVendas: number;
  valorTotal: number;
  ticketMedio: number;
  totalComissoes: number;
}

interface PosicaoEstoque {
  totalVeiculos: number;
  disponiveis: number;
  emPreparacao: number;
  reservados: number;
  vendidos: number;
  custoTotalEstoque: number;
  valorVendaEstoque: number;
  mediaDiasEstoque: number;
}

interface EstoquePorMarca {
  marca: string;
  quantidade: number;
  valorTotal: number;
  mediaDiasEstoque: number;
}

interface VeiculoMaisVendido {
  marca: string;
  modelo: string;
  quantidade: number;
  valorTotal: number;
  precoMedio: number;
}

export function Relatorios() {
  const { showError } = useNotification();

  const [isLoading, setIsLoading] = useState(true);
  const [dataInicio, setDataInicio] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [dataFim, setDataFim] = useState(() => new Date().toISOString().split('T')[0]);

  const [vendasPeriodo, setVendasPeriodo] = useState<VendasPeriodo | null>(null);
  const [vendasVendedor, setVendasVendedor] = useState<VendaPorVendedor[]>([]);
  const [posicaoEstoque, setPosicaoEstoque] = useState<PosicaoEstoque | null>(null);
  const [estoqueMarca, setEstoqueMarca] = useState<EstoquePorMarca[]>([]);
  const [veiculosMaisVendidos, setVeiculosMaisVendidos] = useState<VeiculoMaisVendido[]>([]);

  const fetchData = useCallback(async (signal?: AbortSignal) => {
    setIsLoading(true);
    try {
      const params = `dataInicio=${dataInicio}&dataFim=${dataFim}`;

      const [vendasRes, vendedorRes, estoqueRes, marcaRes, maisVendidosRes] = await Promise.all([
        api.get<VendasPeriodo>(`/relatorios/vendas/periodo?${params}&agrupamento=DIA`, { signal }),
        api.get<VendaPorVendedor[]>(`/relatorios/vendas/vendedor?${params}`, { signal }),
        api.get<PosicaoEstoque>('/relatorios/estoque/posicao', { signal }),
        api.get<EstoquePorMarca[]>('/relatorios/estoque/marca', { signal }),
        api.get<VeiculoMaisVendido[]>(`/relatorios/vendas/veiculos-mais-vendidos?${params}`, { signal }),
      ]);

      setVendasPeriodo(vendasRes.data);
      setVendasVendedor(vendedorRes.data);
      setPosicaoEstoque(estoqueRes.data);
      setEstoqueMarca(marcaRes.data);
      setVeiculosMaisVendidos(maisVendidosRes.data);
    } catch (error: unknown) {
      if (error instanceof Error && error.name !== 'CanceledError') {
        showError('Erro ao carregar relatórios');
      }
    } finally {
      setIsLoading(false);
    }
  }, [dataInicio, dataFim, showError]);

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, [fetchData]);

  const maxVendas = Math.max(...(vendasVendedor.map((v) => v.quantidadeVendas) || [1]));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Relatórios" subtitle="Análise de vendas e estoque">
        <div className="flex items-center gap-3">
          <div className="flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3">
            <Calendar className="h-4 w-4 text-slate-400" />
            <input
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              className="border-none bg-transparent text-sm text-slate-700 focus:outline-none"
            />
            <span className="text-slate-400">até</span>
            <input
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              className="border-none bg-transparent text-sm text-slate-700 focus:outline-none"
            />
          </div>
          <button className={buttonClass('secondary')}>
            <Download className="h-4 w-4" />
            Exportar
          </button>
        </div>
      </PageHeader>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <StatCard
          title="Vendas no Período"
          value={vendasPeriodo?.totais.totalVendas || 0}
          icon={<TrendingUp className="h-[18px] w-[18px]" />}
          delay={0}
        />
        <StatCard
          title="Faturamento"
          value={formatCurrency(vendasPeriodo?.totais.valorTotal || 0)}
          icon={<DollarSign className="h-[18px] w-[18px]" />}
          tone="positive"
          delay={60}
        />
        <StatCard
          title="Veículos Disponíveis"
          value={posicaoEstoque?.disponiveis || 0}
          icon={<Car className="h-[18px] w-[18px]" />}
          delay={120}
        />
        <StatCard
          title="Média em Estoque"
          value={`${formatNumber(posicaoEstoque?.mediaDiasEstoque || 0, 0)} dias`}
          icon={<Clock className="h-[18px] w-[18px]" />}
          delay={180}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Vendas por Vendedor */}
        <Panel title="Vendas por Vendedor" icon={<Users className="h-4 w-4" />} delay={240}>
          <div className="space-y-4">
            {vendasVendedor.map((v) => (
              <div key={v.vendedorId} className="flex items-center gap-4">
                <div className="w-24 truncate text-sm font-medium text-slate-700">
                  {v.vendedorNome.split(' ')[0]}
                </div>
                <div className="flex-1">
                  <div className="h-6 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full bg-primary-500 transition-all"
                      style={{ width: `${(v.quantidadeVendas / maxVendas) * 100}%` }}
                    />
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold tabular-nums text-slate-900">{v.quantidadeVendas} vendas</p>
                  <p className="text-xs tabular-nums text-slate-400">{formatCurrency(v.valorTotal)}</p>
                </div>
              </div>
            ))}
            {vendasVendedor.length === 0 && <EmptyState>Nenhuma venda no período</EmptyState>}
          </div>
        </Panel>

        {/* Estoque por Marca */}
        <Panel title="Estoque por Marca" icon={<Car className="h-4 w-4" />} delay={300}>
          <div className="space-y-3">
            {estoqueMarca.slice(0, 8).map((e, index) => (
              <div key={e.marca} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="grid h-6 w-6 place-items-center rounded-full bg-slate-50 text-xs font-bold text-slate-500 ring-1 ring-slate-100">
                    {index + 1}
                  </span>
                  <span className="font-medium text-slate-800">{e.marca}</span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-bold tabular-nums text-slate-900">{e.quantidade}</span>
                  <span className="ml-2 text-xs tabular-nums text-slate-400">{formatCurrency(e.valorTotal)}</span>
                </div>
              </div>
            ))}
            {estoqueMarca.length === 0 && <EmptyState>Nenhum veículo em estoque</EmptyState>}
          </div>
        </Panel>
      </div>

      {/* Veículos Mais Vendidos */}
      <Panel title="Modelos Mais Vendidos" icon={<BarChart3 className="h-4 w-4" />} delay={360}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left text-[11px] font-bold uppercase tracking-wider text-slate-400">
                <th className="pb-3 font-bold">#</th>
                <th className="pb-3 font-bold">Marca / Modelo</th>
                <th className="pb-3 text-center font-bold">Quantidade</th>
                <th className="pb-3 text-right font-bold">Valor Total</th>
                <th className="pb-3 text-right font-bold">Preço Médio</th>
              </tr>
            </thead>
            <tbody>
              {veiculosMaisVendidos.map((v, index) => (
                <tr key={`${v.marca}-${v.modelo}`} className="border-b border-slate-50 last:border-0">
                  <td className="py-3 font-bold text-slate-400">{index + 1}</td>
                  <td className="py-3 font-semibold text-slate-800">
                    {v.marca} {v.modelo}
                  </td>
                  <td className="py-3 text-center">
                    <Badge tone="primary">{v.quantidade}</Badge>
                  </td>
                  <td className="py-3 text-right font-semibold tabular-nums text-emerald-600">
                    {formatCurrency(v.valorTotal)}
                  </td>
                  <td className="py-3 text-right tabular-nums text-slate-600">{formatCurrency(v.precoMedio)}</td>
                </tr>
              ))}
              {veiculosMaisVendidos.length === 0 && (
                <tr>
                  <td colSpan={5}>
                    <EmptyState>Nenhuma venda no período</EmptyState>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
